'use client';

import React from 'react';
import { styled } from '@linaria/react';
import { animated, useSpring } from 'react-spring';

import useSound from '@/hooks/use-sound';
import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';

import VisuallyHidden from '@/components/VisuallyHidden';

interface Props extends React.HTMLAttributes<HTMLButtonElement> {
  width: number;
  layer: 'top' | 'bottom';
}

function StarCat({ width, layer, ...delegated }: Props) {
  const id = React.useId().replace(/:/g, '');
  const [mood, setMood] = React.useState('normal');

  const prefersReducedMotion = usePrefersReducedMotion();

  React.useEffect(() => {
    if (mood === 'normal') {
      return;
    }

    const timeout = window.setTimeout(
      () => {
        setMood('normal');
      },
      mood === 'happy' ? 220 : 450
    );

    return () => {
      window.clearTimeout(timeout);
    };
  }, [mood]);

  const numOfClicks = React.useRef(0);

  const [playHappy] = useSound('/sounds/cat-press.mp3', {
    volume: 0.6,
  });
  const [playMad] = useSound('/sounds/cat-on.mp3', { volume: 0.3 });

  const headStyle = useSpring({
    transform: prefersReducedMotion
      ? 'rotate(0deg)'
      : mood === 'happy'
        ? 'rotate(6deg)'
        : mood === 'mad'
          ? 'rotate(-5deg)'
          : 'rotate(0deg)',
    transformOrigin: 'center center',
    config: {
      tension: mood === 'mad' ? 350 : 170,
      friction: mood === 'normal' ? 25 : mood === 'mad' ? 10 : 10,
    },
  });

  return (
    <button
      {...delegated}
      onClick={() => {
        if (mood === 'normal') {
          numOfClicks.current += 1;

          const isHappyAboutClick = numOfClicks.current % 8 !== 0;
          setMood(isHappyAboutClick ? 'happy' : 'mad');

          isHappyAboutClick ? playHappy() : playMad();
        }
      }}
    >
      <Svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        width={width}
        viewBox="0 0 399 472"
        aria-hidden="true"
      >
        <mask
          id={`${id}-mask`}
          style={{ maskType: 'alpha' }}
          width="314"
          height="320"
          x="168"
          y="141"
          maskUnits="userSpaceOnUse"
        >
          <path
            fill="#C4C4C4"
            d="M168.196 379.181H420.076V622.857H168.196z"
            transform="rotate(-70.611 168.196 379.181)"
          ></path>
        </mask>
        {layer === 'bottom' ? (
          <g mask={`url(#${id}-mask)`}>
            <path
              fill="#fff"
              stroke="var(--color-outline)"
              strokeWidth="10"
              d="M456.06 447.462c-68.217-6.823-127.014-26.426-174.079-69.961-39.169-36.231-53.406-51.886-39.7-99.789 15.825-55.31 33.898-63.644 94.143-68.039 65.215-4.757 125.106 18.51 182.786 58.349 20.481 14.146 31.776 23.601 44.597 41.786 19.539 27.718 14.051 53.196 5.806 77.601-8.329 24.653-19.684 48.135-53.127 56.859-21.349 5.569-35.93 5.645-60.426 3.194z"
            ></path>
          </g>
        ) : (
          <>
            {/* Arms */}
            <path
              fill="#fff"
              d="M350.844 376.47c-4.779 29.526-96.148 5.616-96.148 5.616l-16-33 6-41.5S252.824 329.583 281 344c35 0 72.133 18.331 69.844 32.47z"
            ></path>
            <path
              stroke="var(--color-outline)"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="10"
              d="M296.06 347.518s59.563-.574 54.784 28.952c-4.779 29.526-62.617 15.22-62.617 15.22s-39.03-10.104-46.531-27.104c-7.5-17-4-50-4-50"
            ></path>
            <path
              fill="#fff"
              d="M375.738 277.062c-6.754 29.138-63.499 10.973-63.499 10.973l10.787-43.544s59.466 3.433 52.712 32.571z"
            ></path>
            <path
              stroke="var(--color-outline)"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="10"
              d="M312.239 288.035s56.745 18.165 63.499-10.973c6.754-29.138-52.712-32.571-52.712-32.571"
            ></path>
            {/* Head */}
            <animated.g style={headStyle}>
              <path
                fill="#fff"
                stroke="var(--color-outline)"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="12"
                d="M274.468 303.833c-60.778 43.563-129.657 65.408-178.119 18.305-26.47-25.729-42.082-49.209-38.523-84.759 0 0-37.906-37.801-46.532-69.205-2.506-9.126-4.576-13.051-4.23-23.862.178-5.547.863-9.34 4.594-13.45 3.29-3.626 3.88-4.176 7.277-6.287 27.27-16.945 85.464 10.596 85.464 10.596 12.343-13.177 48.735-34.45 64.037-41.187a117.579 117.579 0 016.462-2.618s7.051-45.275 23.845-67.732c3.92-5.243 7.63-9.46 13.442-12.473 4.667-2.42 6.793-3.702 12.039-3.36 7.976.52 18.023 5.12 27.152 14.328 21.538 21.729 38.142 74.419 38.142 74.419 33.625 14.091 46.474 37.476 58.623 69.682 22.716 60.217-21.362 100.11-73.673 137.603z"
              ></path>
              {/* Eye star */}
              <path
                fill="#FFE62A"
                d="M121.285 226.052l2.934 18.675s.195 1.469-.02 4.328c-.295 3.932-4.711 7.243-4.711 7.243l-18.487 14.723s-5.796 5.247-4.917 9.529c.946 4.603 11.435 5.835 11.435 5.835l23.879-.812s1.811-.574 4.525 2.05c2.714 2.623 3.176 19.065 6.392 29.473 1.495 4.839 3.745 10.436 8.427 10.947 5.173.565 10.48-11.912 10.48-11.912l7.247-17.988s2.917-6.874 4.457-8.164c2.179-1.825 9.616-.581 9.616-.581l26.047 2.163s7.636-.971 9.233-5.197c1.685-4.46-5.449-12.258-5.449-12.258s-7.962-7.281-14.755-12.41l-.153-.116c-2.508-1.893-7.71-5.82-7.984-7.235-.28-1.444 6.518-19.16 6.518-19.16 1.423-4.965 6.914-15.509 3.452-18.862-2.496-2.417-7.195-1.474-12.77.377-5.575 1.852-18.327 9.235-18.327 9.235s-6.735 2.985-9.502 2.662c-2.476-.289-5.152-2.187-5.152-2.187l-15.996-10.56s-8.083-4.952-12.413-2.536c-4.563 2.545-4.006 12.738-4.006 12.738z"
              ></path>
              {/* Ears */}
              <path
                fill="#FBCECA"
                d="M37.232 175.569c9.985 16.071 29.58 38.132 29.58 38.132s5.462-22.101 9.784-32.569c3.9-9.444 15.327-23.651 15.327-23.651s-38.205-19.234-54.514-9.1c0 0-10.162 11.118-.177 27.188zM245.347 44.637c8.883 16.705 19.002 51.084 19.002 51.084s-19.89-5.545-31.096-7.073c-10.111-1.378-30.042.504-30.042.504s4.152-48.922 21.071-57.916c0 0 12.181-3.304 21.065 13.401z"
              ></path>
              {/* Eyes */}
              {mood === 'happy' ? (
                <>
                  <path
                    stroke="var(--color-outline)"
                    strokeLinecap="round"
                    strokeWidth="10"
                    d="M261.103 201.517s-5.373-17.544 3.012-23.054c8.385-5.509 22.359 7.292 22.359 7.292M145.58 273.284s-5.895-17.201 3.012-23.054c8.908-5.853 22.36 7.292 22.36 7.292"
                  ></path>
                </>
              ) : (
                <>
                  <circle
                    cx="155.264"
                    cy="260.614"
                    r="18"
                    fill="var(--color-outline)"
                    transform="rotate(-31.855 155.264 260.614)"
                  ></circle>
                  <circle
                    cx="270.781"
                    cy="188.838"
                    r="18"
                    fill="var(--color-outline)"
                    transform="rotate(-31.855 270.781 188.838)"
                  ></circle>
                  {mood !== 'mad' && (
                    <path
                      stroke="#fff"
                      strokeLinecap="round"
                      strokeWidth="6"
                      d="M146.036 262.816s-.397-1.904.201-4.05c.528-1.895 1.407-2.836 1.407-2.836"
                    ></path>
                  )}
                  {mood !== 'mad' && (
                    <path
                      stroke="#fff"
                      strokeLinecap="round"
                      strokeWidth="6"
                      d="M261.553 191.04s-.397-1.904.201-4.049c.529-1.896 1.408-2.837 1.408-2.837"
                    ></path>
                  )}
                </>
              )}
              {mood === 'mad' && (
                <>
                  <path
                    fill="var(--color-outline)"
                    stroke="#FFE62A"
                    strokeLinecap="round"
                    strokeWidth="4"
                    d="M133.292 233.912a6 6 0 10-.613 11.984l43.772 2.239a6 6 0 00.613-11.984l-43.772-2.239z"
                  ></path>
                  <path
                    fill="var(--color-outline)"
                    stroke="#fff"
                    strokeLinecap="round"
                    strokeWidth="4"
                    d="M240.665 198.667a6 6 0 0011.312 4.005l14.628-41.316a6 6 0 00-11.312-4.005l-14.628 41.316z"
                  ></path>
                </>
              )}
              {/* Mouth */}
              {mood === 'happy' && (
                <>
                  <path
                    fill="#222"
                    stroke="#222"
                    d="M241.341 270.302c-8.392 6.39-14.988-4.228-14.988-4.228l5.489-11.062 12.349-.019s5.541 8.919-2.85 15.309z"
                  ></path>
                  <path
                    fill="#FF9BA7"
                    d="M241.16 258.641c2.332 3.753.885 6.514-1.93 8.263-2.815 1.749-5.931 1.823-8.263-1.93-2.331-3.753-1.94-8.213.875-9.962 2.815-1.749 6.987-.124 9.318 3.629z"
                  ></path>
                </>
              )}
              {mood === 'mad' ? (
                <path
                  stroke="var(--color-outline)"
                  strokeLinecap="round"
                  strokeWidth="6"
                  d="M229.53 239.92c-7.904.659-17.202 5.019-15.148 11.299 1.61 4.924 9.162 6.158 12.348 6.461.691.066 1.171.782.975 1.448-.924 3.136-2.431 10.696 4.223 11.415 8.417.911 10.515-9.837 10.515-9.837"
                ></path>
              ) : (
                <path
                  stroke="#000"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="5"
                  d="M217.525 266.078s11.053.41 13.475-4.841c2.146-4.654-3.2-12.728-3.2-12.728s6.088 7.999 11.269 7.715c5.202-.286 9.034-9.146 9.034-9.146"
                ></path>
              )}
              {/* Head stripes */}
              <path
                fill="#DEDEDE"
                d="M143.161 160.731c-10.609-7.581-23.023-31.06-23.023-31.06s3.91-3.612 6.679-5.539c2.427-1.688 6.51-3.844 6.51-3.844s20.42 18.604 23.37 32.45c.953 4.474 1.849 8.219-.752 10.81-3.256 3.244-8.316.376-12.784-2.817zM169.264 144.092c-10.337-8.398-20.028-33.143-20.028-33.143s4.438-2.596 7.376-4.253c2.575-1.452 7.234-3.56 7.234-3.56s18.321 19.668 20.017 34.1c.534 4.543 1.397 8.197-1.436 10.532-3.548 2.922-8.9-.213-13.163-3.676z"
              ></path>
            </animated.g>
          </>
        )}
      </Svg>
      <VisuallyHidden>
        Illustration of a cat. Triggering this button pets the cat.
        This is a purely cosmetic effect.
      </VisuallyHidden>
    </button>
  );
}

const Svg = styled.svg`
  --color-outline: #222;

  html[data-color-mode='dark'] & {
    --color-outline: #000;
  }
`;

export default StarCat;
